import * as THREE from 'three';
import { LineLevels } from "../Shapes/LineLevels";
import { NetworkNode, RouterNetworkTree } from "../../../../../../services/back/infrastructure-schema.service";

export const levelArray: { [depth: number]: number } = {};

export function ManagerLevel(
	scene: THREE.Scene,
	data: Array<RouterNetworkTree>,
	globalWidth: number,
	z: number = -11,
	isRoot: boolean = true,
	levelInstances: LineLevels[] = []
): LineLevels[] {
	// Initialisation du premier niveau à -13
	if (isRoot) {
		levelArray[0] = z;

		// Calculer les décalages pour tous les niveaux récursivement
		calculateDepthOffsets(data, 1);
	}

	// Dessiner les niveaux uniquement à la fin de l'exécution (si nous sommes à la racine)
	if (isRoot) {
		drawLevel(scene, levelArray, levelInstances, globalWidth);
	}

	return levelInstances;
}

function calculateDepthOffsets(data: RouterNetworkTree[], currentDepth: number): void {
	// Si nous avons déjà calculé ce niveau, pas besoin de continuer
	if (currentDepth > 0 && levelArray[currentDepth]) {
		return;
	}

	// Parcourir tous les réseaux d'intercommunication pour trouver les routeurs du niveau suivant
	const nextLevelRouters: RouterNetworkTree[] = [];

	for (const router of data) {
		for (const network of router.connectedNetworks) {
			// Ne considérer que les réseaux d'intercommunication
			if (network.network.network.role === "intercommunication") {
				// Ajouter les routeurs connectés au niveau suivant
				if (network.connectedRouters && network.connectedRouters.length > 0) {
					nextLevelRouters.push(...network.connectedRouters);
				}
			}
		}
	}

	// Pour le niveau actuel, trouver l'offset Z basé sur le nombre maximum de serveurs
	if (currentDepth > 0) {
		const baseOffset = nextLevelRouters.length > 0 ? 14 : 10;
		const prevZ = levelArray[currentDepth - 1];
		let zOffset = findMaxServersOffset(data, currentDepth);
		levelArray[currentDepth] = prevZ + baseOffset + zOffset;
	}

	// S'il y a des routeurs au niveau suivant, calculer leurs offsets
	if (nextLevelRouters.length > 0) {
		calculateDepthOffsets(nextLevelRouters, currentDepth + 1);
	}
}

function findMaxServersOffset(routers: RouterNetworkTree[], depth: number): number {
	let maxServers = 0;
	// Parcourir tous les routeurs
	for (const router of routers) {
		// Pour chaque réseau connecté
		for (const network of router.connectedNetworks) {
			// Ne considérer que les réseaux qui ne sont pas d'intercommunication
			if (network.network.network.role !== "intercommunication") {
				if (network.network.servers && Array.isArray(network.network.servers)) {
					maxServers = Math.max(maxServers, network.network.servers.length);
				}
			}
		}
	}
	if (maxServers === 0) {maxServers = 1;}

	// Calcul de l'offset
	return Math.min(Math.ceil(maxServers / 4) * 4 + 2, 10);
}

function drawLevel(
	scene: THREE.Scene,
	levelArray: { [depth: number]: number },
	levelInstances: LineLevels[],
	globalWidth: number
): void {
	const keys = Object.keys(levelArray).map(Number);
	const lastKey = Math.max(...keys);

	for (let key in levelArray) {
		if (key === "0") {
			const line = new LineLevels(scene, levelArray[key] - 4, key, globalWidth);
			levelInstances.push(line);
		} else if (key !== lastKey.toString()) {
			const line = new LineLevels(scene, levelArray[key], key, globalWidth);
			levelInstances.push(line);
		}
	}
}
